import { useEffect, useState, type CSSProperties } from 'react';
import { m, AnimatePresence } from 'framer-motion';
import { Player, GameState, GameSettings, getCategoriesForLobby } from '@brainfart/shared';
import { CategoryChip } from './CategoryChip';
import { QuizSetupPanel, LengthTimerSections, lengthOptionsFor } from './QuizSetupPanel';
import { Modal } from './Modal';
import { ScreenHeader } from './ScreenHeader';

interface LobbyScreenProps {
  game: GameState;
  me: Player;
  isHost: boolean;
  onStart: () => void;
  onUpdateSettings: (settings: Partial<GameSettings>) => void;
  onLeaveHome: () => void;
}

function categoryColor(category: string): string {
  const meta = getCategoriesForLobby().find(c => c.key === category);
  return meta?.color || '#888';
}

function categoryIcon(category: string): string {
  const meta = getCategoriesForLobby().find(c => c.key === category);
  return meta?.icon || '❓';
}

function inviteMessage(hostName: string, categories: string[], link: string): string {
  const topic = categories.length === 1 ? ` about ${categories[0]}` : ' at trivia';
  return `${hostName} thinks they can beat you${topic}. Embarrassing if true.\nJoin here: ${link}`;
}

export function LobbyScreen({ game, me, isHost, onStart, onUpdateSettings, onLeaveHome }: LobbyScreenProps) {
  const [copied, setCopied] = useState(false);

  const inviteLink = `${window.location.origin}${window.location.pathname}?room=${game.code}`;
  const shareText = inviteMessage(me.name, game.settings.categories, inviteLink);

  const copyInviteLink = async () => {
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ text: shareText });
        return;
      } catch {
        return;
      }
    }
    navigator.clipboard.writeText(shareText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && isHost && game.players.length >= 2) {
        onStart();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isHost, game.players.length, onStart]);

  return (
    <m.div
      className="lobby-screen"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}
    >
      <ScreenHeader
        left={
        <button
          onClick={onLeaveHome}
          style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
        >
          <m.span
            animate={{ rotate: [0, 5, -5, 0] }}
            transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
            style={{ fontSize: 'clamp(1.5rem, 5vw, 2rem)', flexShrink: 0 }}
          >
            🧠
          </m.span>
          <h1 style={{ fontSize: 'clamp(1.1rem, 4vw, 1.5rem)', fontWeight: 700, background: 'linear-gradient(135deg, #FFC93C, #FF9F1C)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', whiteSpace: 'nowrap' }}>
            BrainFart.fun
          </h1>
        </button>
        }
        right={
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', minWidth: 0 }}>
          {/* Invite Link */}
          <m.button
            className="room-code"
            animate={{ scale: copied ? 1 : [1, 1.02, 1] }}
            transition={{ duration: 2, repeat: copied ? 0 : Infinity }}
            style={{
              background: copied ? 'rgba(57,255,136,0.15)' : 'linear-gradient(135deg, rgba(255,201,60,0.2), rgba(255,159,28,0.1))',
              border: copied ? '1px solid rgba(57,255,136,0.4)' : '1px solid rgba(255,201,60,0.3)',
              padding: 'clamp(0.4rem, 1.5vw, 0.5rem) clamp(0.75rem, 3vw, 1.25rem)',
              borderRadius: '100px',
              fontWeight: 700,
              color: copied ? '#39FF88' : '#FFC93C',
              cursor: 'pointer',
              userSelect: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              maxWidth: '100%',
            }}
            onClick={copyInviteLink}
            title={inviteLink}
          >
            <span style={{ fontFamily: 'monospace', fontSize: 'clamp(0.95rem, 3.5vw, 1.1rem)', letterSpacing: '0.15em' }}>{game.code}</span>
            <span style={{ fontSize: 'clamp(0.7rem, 2.5vw, 0.8rem)', fontWeight: 600, whiteSpace: 'nowrap' }}>
              {copied ? 'Copied!' : typeof navigator.share === 'function' ? '🔗 Share invite' : '🔗 Copy invite'}
            </span>
          </m.button>

          {/* Player Count */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#888', fontSize: '0.9rem', flexShrink: 0 }}>
            <span>{game.players.length}</span>
            <span style={{ color: '#444' }}>/</span>
            <span>{game.settings.maxPlayers}</span>
            <span>👥</span>
          </div>
        </div>
        }
      />

      {/* Main content. The category/theme picker lives right here on the page;
          the format (length + timer) is chosen in a modal when the host hits
          Start. paddingBottom leaves room for the fixed Start bar below. */}
      <main style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: 'clamp(0.85rem, 4vw, 2rem)', paddingBottom: '7.5rem', maxWidth: '880px', margin: '0 auto', width: '100%' }}>
        <PlayerStrip game={game} me={me} />

        {isHost ? (
          <QuizSetup game={game} onUpdateSettings={onUpdateSettings} onStart={onStart} />
        ) : (
          <QuizSetupSummary game={game} />
        )}
      </main>

      {/* Guests can't start — a fixed reminder mirrors the host's Start bar.
          (The host's own fixed Start bar lives in <QuizSetup>, which owns the
          length/timer state the start modal needs.) */}
      {!isHost && (
        <div style={FIXED_BAR_STYLE}>
          <div style={{
            width: '100%', maxWidth: 480, padding: '1.15rem', textAlign: 'center',
            background: '#211E3B', border: '1px solid #2E2B45', borderRadius: '12px', color: '#888',
          }}>
            {game.players.find(p => p.isHost)?.name || 'The host'} holds all the power. This was a mistake.
          </div>
        </div>
      )}
    </m.div>
  );
}

// Shared chrome for the fixed bottom bar (host Start / guest reminder).
const FIXED_BAR_STYLE: CSSProperties = {
  position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 50,
  padding: '0.85rem clamp(0.85rem, 4vw, 2rem)',
  paddingBottom: 'max(0.85rem, env(safe-area-inset-bottom))',
  background: 'linear-gradient(to top, rgba(15,13,31,0.98) 55%, rgba(15,13,31,0))',
  backdropFilter: 'blur(8px)',
  display: 'flex', justifyContent: 'center',
};

// Slim horizontal strip instead of a big empty-slot grid — who's in the room
// matters less at this point than what's about to be played, so this should
// read as a quiet confirmation, not the headline of the screen.
function PlayerStrip({ game, me }: { game: GameState; me: Player }) {
  return (
    <div style={{ marginBottom: '0.5rem' }}>
      <p style={{ fontSize: '0.7rem', fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.5rem' }}>
        Who's here
      </p>
      <div style={{ display: 'flex', gap: '0.65rem', overflowX: 'auto', paddingBottom: '0.35rem' }}>
        <AnimatePresence>
          {game.players.map(player => (
            <m.div
              key={player.id}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.3rem', flexShrink: 0, width: '58px' }}
            >
              <div style={{
                position: 'relative',
                width: '46px',
                height: '46px',
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${player.color}33, ${player.color}11)`,
                border: `2px solid ${player.id === me.id ? player.color : (player.connected ? '#2E2B45' : '#444')}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem',
                filter: player.connected ? 'none' : 'grayscale(1)',
              }}>
                {player.avatar || '🧠'}
                {player.connected && (
                  <m.div
                    animate={{ scale: [1, 1.3, 1], opacity: [1, 0.5, 1] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    style={{ position: 'absolute', bottom: '-2px', right: '-2px', width: '10px', height: '10px', borderRadius: '50%', background: '#39FF88', border: '2px solid #15132A' }}
                  />
                )}
              </div>
              <span style={{
                fontSize: '0.7rem',
                color: player.id === me.id ? '#FFC93C' : '#999',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '58px',
              }}>
                {player.isHost && '👑'}{player.name}
              </span>
            </m.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}

// Both the host's picker and everyone else's read-only mirror render the
// exact same grid, so non-hosts watch selections update live instead of
// seeing a flat summary that lags behind what the host is actually doing.
function CategoryGrid({ categories, selected, onToggle }: {
  categories: { key: string; title: string; icon: string; color: string; count: number }[];
  selected: string[];
  onToggle?: (category: string) => void;
}) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.25rem' }}>
      {categories.map(({ key, title, icon, color }) => (
        <CategoryChip
          key={key}
          icon={icon}
          title={title}
          color={color}
          active={selected.includes(key)}
          onClick={onToggle ? () => onToggle(key) : undefined}
        />
      ))}
    </div>
  );
}

function QuizSetup({ game, onUpdateSettings, onStart }: { game: GameState; onUpdateSettings: (settings: Partial<GameSettings>) => void; onStart: () => void }) {
  const allCategories = game.availableCategories;

  // Start with NO categories selected — host must pick.
  const [selectedCategories, setSelectedCategories] = useState<string[]>(() => game.settings.categories || []);
  const [selectedThemes, setSelectedThemes] = useState<string[]>(() => game.settings.themes || []);
  const [triviaRounds, setTriviaRounds] = useState(() => Math.max(1, game.settings.rounds));
  const [roundTimeLimit, setRoundTimeLimit] = useState(() => game.settings.roundTimeLimit || 20);

  const available = allCategories
    .filter(c => selectedCategories.includes(c.key))
    .reduce((sum, c) => sum + c.count, 0);

  // Snap length to a valid pack when the available pool shrinks.
  useEffect(() => {
    const opts = lengthOptionsFor(available);
    if (!opts.some(o => o.n === triviaRounds)) {
      const fit = [...opts].reverse().find(o => o.n <= triviaRounds) || opts[0];
      setTriviaRounds(fit.n);
    }
  }, [available]);

  useEffect(() => {
    onUpdateSettings({
      categories: selectedCategories,
      themes: selectedThemes,
      rounds: triviaRounds,
      roundTimeLimit,
    });
  }, [selectedCategories, selectedThemes, triviaRounds, roundTimeLimit]);

  const toggleCategory = (cat: string) => {
    setSelectedCategories(prev => {
      if (prev.includes(cat)) {
        return prev.filter(c => c !== cat); // allow empty selection
      }
      return [...prev, cat];
    });
  };

  const [startOpen, setStartOpen] = useState(false);
  const canStart = game.players.length >= 2 && selectedCategories.length > 0;
  const noCategories = selectedCategories.length === 0;

  return (
    <>
      {/* Category tiles + theme filters live right here on the page. */}
      <m.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        style={{ marginTop: '1.25rem', width: '100%' }}
      >
        <QuizSetupPanel
          categories={allCategories}
          selectedCategories={selectedCategories}
          onToggleCategory={toggleCategory}
          selectedThemes={selectedThemes}
          setSelectedThemes={setSelectedThemes}
          available={available}
          rounds={triviaRounds}
          setRounds={setTriviaRounds}
          timer={roundTimeLimit}
          setTimer={setRoundTimeLimit}
          hideLengthTimer
        />
      </m.div>

      {/* Fixed Start bar. Length + timer are chosen in the modal it opens. */}
      <div style={FIXED_BAR_STYLE}>
        <m.button
          onClick={() => !noCategories && setStartOpen(true)}
          disabled={!canStart}
          className="btn btn-primary btn-lg"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ width: '100%', maxWidth: 480, padding: '1.15rem', fontSize: '1.25rem' }}
          title={noCategories ? 'Pick at least one topic to start' : undefined}
        >
          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem' }}>
            <m.span animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 1, repeat: Infinity }}>🚀</m.span>
            {noCategories
              ? 'Pick a topic →'
              : !canStart
                ? 'Waiting for players…'
                : 'Start Game'}
          </span>
        </m.button>
      </div>

      {/* Format modal — pick length + timer, then actually kick off the game. */}
      <Modal open={startOpen} onClose={() => setStartOpen(false)} title="🎮 Set the format">
        <LengthTimerSections
          available={available}
          rounds={triviaRounds}
          timer={roundTimeLimit}
          onRounds={setTriviaRounds}
          onTimer={setRoundTimeLimit}
        />
        <button
          onClick={() => { setStartOpen(false); onStart(); }}
          className="btn btn-primary btn-lg"
          style={{ width: '100%', padding: '1.15rem', fontSize: '1.15rem' }}
        >
          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem' }}>
            🚀 Start the quiz
          </span>
        </button>
      </Modal>
    </>
  );
}

function QuizSetupSummary({ game }: { game: GameState }) {
  const allCats = getCategoriesForLobby();
  const selected = game.settings.categories;
  const chosen = allCats.filter(c => selected.includes(c.key));

  return (
    <m.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{ marginTop: '1.25rem', width: '100%' }}
    >
      <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#ccc', marginBottom: '0.75rem' }}>
        🎯 {game.players.find(p => p.isHost)?.name || 'Host'} {chosen.length ? 'picked these topics' : 'is picking topics…'}
      </h3>
      {chosen.length > 0 && <CategoryGrid categories={chosen} selected={selected} />}
      <p style={{ color: '#666', fontSize: '0.8rem', marginTop: '-0.5rem' }}>
        🧠 {game.settings.rounds} trivia questions · ⏱️ {game.settings.roundTimeLimit}s each
        {game.settings.themes && game.settings.themes.length > 0 && ` · 🎯 ${game.settings.themes.length} theme filter(s)`}
      </p>
    </m.div>
  );
}